import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useFacility } from "@/context/FacilityContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import BulkPtoTab from "@/components/BulkPtoTab";
import { useAuth } from "@/context/AuthContext";
import { format, parseISO } from "date-fns";
import { mdy, mdyDow, mdyTime } from "@/lib/dateFmt";
import {
  ClipboardList, Check, X, AlertTriangle, Building2, Repeat2, ArrowLeftRight,
  HandHelping, Calendar as CalendarIcon, Clock, Wrench, PhoneCall,
  Calculator, TrendingDown, TrendingUp, Users2, ChevronDown, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_COLORS = {
  pending:  "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300",
  approved: "bg-sky-100 text-sky-800 border-sky-300",
  confirmed:"bg-sky-100 text-sky-800 border-sky-300",
  denied:   "bg-red-100 text-red-700 border-red-300",
};

function Flag({ ok, label }) {
  return (
    <span className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] font-semibold ${
      ok
        ? "bg-emerald-50 border-emerald-200 text-emerald-800"
        : "bg-amber-50 border-amber-200 text-amber-800"
    }`}>
      {ok ? <Check size={11} /> : <AlertTriangle size={11} />}
      {label}: {ok ? "yes" : "no"}
    </span>
  );
}

const safeParse = (s) => { try { return s ? parseISO(s) : null; } catch { return null; } };
const fmt = (s, pattern, fallback = "—") => {
  const d = safeParse(s);
  if (!d) return fallback;
  // Central redirect: map common legacy patterns to M/D/YYYY equivalents so
  // every existing `fmt(iso, "MMM d, yyyy HH:mm")` call comes out uniformly.
  const p = pattern
    .replace("MMM d, yyyy HH:mm", "M/d/yyyy, HH:mm")
    .replace("MMM d, yyyy 'at' h:mm a", "M/d/yyyy 'at' h:mm a")
    .replace("MMM d, yyyy", "M/d/yyyy")
    .replace("EEE, MMM d, yyyy", "EEE M/d/yyyy")
    .replace("EEE, MMM d", "EEE M/d/yyyy")
    .replace("MMM d", "M/d/yyyy");
  return format(d, p);
};

const REASON_LABELS = { sick: "Sick", fmla: "FMLA", other: "Other" };

// --- Pay-period math for PTO tab filter --------------------------------------
// Auburn + Solano post schedules monthly; every other facility runs bi-weekly
// off the shared Sunday anchor. `pp_for_date` returns a stable id + display
// label so we can group PTO requests into left-to-right pay-period tabs.
const MONTHLY_FACILITIES = new Set(["auburn", "solano"]);
const PP_BASE_ANCHOR = new Date("2026-01-04T00:00:00");  // Sunday, matches backend
const pp_for_date = (iso, facilityId) => {
  if (!iso) return null;
  try {
    const d = parseISO(iso);
    if (MONTHLY_FACILITIES.has(facilityId)) {
      const y = d.getFullYear(), m = d.getMonth() + 1;
      const id = `mo-${y}-${String(m).padStart(2, "0")}`;
      const label = format(new Date(y, m - 1, 1), "MMMM yyyy");
      // Sort-key = first day of the month.
      return { id, label, startIso: `${y}-${String(m).padStart(2, "0")}-01` };
    }
    const daysFromBase = Math.floor((d - PP_BASE_ANCHOR) / 86400000);
    const idx = Math.floor(daysFromBase / 14);
    const start = new Date(PP_BASE_ANCHOR); start.setDate(start.getDate() + idx * 14);
    const end   = new Date(start); end.setDate(end.getDate() + 13);
    const startIso = format(start, "yyyy-MM-dd");
    const id = `pp-${startIso}`;
    const label = `${format(start, "M/d")} – ${format(end, "M/d/yyyy")}`;
    return { id, label, startIso };
  } catch {
    return null;
  }
};

// --- PTO Calculator (per-request review panel) ---------------------------------
// Walks the employee's ordered future PTO commitments and shows, for every
// request, the running balance "as it sits" (no accrual yet) AND "with accrual
// through this pay period". Managers can see whether the employee will run
// dry — and if the deficit is rescued by the next PP's accrual bump.
function PtoCalculator({ ctx, thisRequestId, requestId }) {
  const [balance, setBalance] = React.useState("");
  const [open, setOpen] = React.useState(true);
  if (!ctx) return null;
  const accrual = ctx.accrual || { accrual_per_pp: 8, tier_label: "Default", hire_date: null };
  const cur = parseFloat(balance);
  const hasBalance = !isNaN(cur);
  const rate = Number(accrual.accrual_per_pp || 8);
  const commitments = ctx.future_commitments || [];

  // Walk the timeline once so the tiles and the summary agree.
  let runningUsed = 0;
  const tiles = commitments.map((c) => {
    runningUsed += Number(c.pto_hours || 0);
    const asOfNow  = hasBalance ? cur - runningUsed : null;
    const withAccr = hasBalance ? cur - runningUsed + rate * (c.pp_crossings_before || 0) : null;
    return { ...c, asOfNow, withAccr, cumulativeUsed: runningUsed };
  });
  // The specific request the manager is reviewing (highlight its tile).
  const thisTile = tiles.find(t => t.request_id === thisRequestId);
  // Worst-case landing after every commitment (assuming approvals).
  const finalAsOfNow  = hasBalance ? cur - runningUsed : null;
  const finalWithAccr = hasBalance && commitments.length
    ? cur - runningUsed + rate * (commitments[commitments.length - 1].pp_crossings_before || 0)
    : finalAsOfNow;

  const fmtHours = (h) => `${h >= 0 ? "" : "−"}${Math.abs(h).toFixed(2)}h`;
  const dateLabel = (iso) => {
    try { return mdyDow(iso); } catch { return iso; }
  };

  return (
    <div
      className="mt-3 rounded-xl border-2 border-indigo-200 bg-gradient-to-br from-indigo-50 to-white overflow-hidden"
      data-testid={`pto-calc-${requestId}`}
    >
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-3 py-2 hover:bg-indigo-50/60 transition-colors"
        data-testid={`pto-calc-toggle-${requestId}`}
      >
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 rounded-md bg-indigo-600 text-white flex items-center justify-center">
            <Calculator size={13} />
          </span>
          <span className="font-display font-semibold text-slate-800 text-sm">PTO Calculator</span>
          <span className="text-[11px] uppercase tracking-widest text-indigo-700 font-bold bg-indigo-100 border border-indigo-200 rounded px-1.5 py-0.5">
            {rate}h / pay period
          </span>
        </div>
        {open ? <ChevronDown size={14} className="text-indigo-500" /> : <ChevronRight size={14} className="text-indigo-500" />}
      </button>
      {open && (
        <div className="px-3 pb-3 pt-1 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="rounded-lg border border-slate-200 bg-white p-3">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Employee accrual tier</div>
              <div className="text-sm font-medium text-slate-800 mt-0.5">{accrual.tier_label}</div>
              <div className="text-[11px] text-slate-500 mt-1">
                {accrual.hire_date
                  ? <>Hired {mdy(accrual.hire_date)} · {accrual.service_months} mo of service</>
                  : <>No hire date on file — using 8 h/PP default. Add a hire date on the employee&apos;s profile to enable exact math.</>}
              </div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-3">
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold" htmlFor={`bal-${requestId}`}>
                Current PTO balance (hours)
              </label>
              <input
                id={`bal-${requestId}`}
                data-testid={`pto-balance-${requestId}`}
                type="number"
                min="0"
                step="0.25"
                value={balance}
                onChange={(e) => setBalance(e.target.value)}
                placeholder="e.g. 96"
                className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-indigo-400"
              />
              <div className="text-[11px] text-slate-500 mt-1">
                Type the employee&apos;s current bank. Tiles below update automatically.
              </div>
            </div>
          </div>

          {hasBalance && (
            <div className="rounded-lg bg-white border border-slate-200 divide-y divide-slate-100" data-testid={`pto-calc-results-${requestId}`}>
              <div className="flex items-center justify-between px-3 py-2 text-sm">
                <span className="text-slate-600">Current balance</span>
                <span className="font-semibold tabular-nums">{cur.toFixed(2)} h</span>
              </div>
              {thisTile && (
                <div className={`flex items-center justify-between px-3 py-2 text-sm ${thisTile.asOfNow < 0 ? "bg-rose-50" : ""}`}>
                  <span className="text-slate-800 font-medium">After THIS request ({thisTile.pto_hours.toFixed(2)}h)</span>
                  <span className={`font-bold tabular-nums ${thisTile.asOfNow < 0 ? "text-rose-700" : "text-emerald-700"}`}>
                    {thisTile.asOfNow.toFixed(2)} h
                  </span>
                </div>
              )}
              <div className={`flex items-center justify-between px-3 py-2 text-sm ${finalAsOfNow < 0 ? "bg-rose-50" : ""}`}>
                <span className="text-slate-800 font-medium">After ALL future requests ({runningUsed.toFixed(2)}h total, no accrual)</span>
                <span className={`font-bold tabular-nums ${finalAsOfNow < 0 ? "text-rose-700" : "text-emerald-700"}`}>
                  {finalAsOfNow.toFixed(2)} h
                </span>
              </div>
              <div className={`flex items-center justify-between px-3 py-2 text-sm ${finalWithAccr < 0 ? "bg-rose-50" : "bg-emerald-50/40"}`}>
                <span className="text-slate-800 font-medium">After ALL future requests (with accrual through then)</span>
                <span className={`font-bold tabular-nums ${finalWithAccr < 0 ? "text-rose-700" : "text-emerald-700"}`}>
                  {finalWithAccr.toFixed(2)} h
                </span>
              </div>
            </div>
          )}

          {hasBalance && commitments.length > 0 && (
            <div data-testid={`pto-timeline-${requestId}`}>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1.5 flex items-center gap-1.5">
                <CalendarIcon size={11} className="text-indigo-600" /> Timeline · each future request in date order
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {tiles.map((t) => {
                  const isThis = t.request_id === thisRequestId;
                  const shortByAsOf = t.asOfNow < 0;
                  const shortByWith = t.withAccr < 0;
                  const rescued = shortByAsOf && !shortByWith;
                  return (
                    <div
                      key={t.request_id}
                      data-testid={`pto-tile-${requestId}-${t.request_id}`}
                      className={`rounded-lg border-2 p-2.5 ${
                        isThis ? "border-indigo-400 ring-2 ring-indigo-200/60" : "border-slate-200"
                      } ${
                        shortByWith ? "bg-rose-50" : rescued ? "bg-amber-50" : "bg-emerald-50/60"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-[11px] font-semibold text-slate-800">
                          {dateLabel(t.first_date)}
                          {t.dates.length > 1 && (
                            <span className="text-slate-500 font-normal"> → {dateLabel(t.last_date)}</span>
                          )}
                        </div>
                        <span className={`text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded ${
                          t.status === "approved"
                            ? "bg-emerald-100 border border-emerald-300 text-emerald-800"
                            : "bg-amber-100 border border-amber-300 text-amber-800"
                        }`}>
                          {t.status}
                        </span>
                      </div>
                      {isThis && (
                        <div className="text-[9px] uppercase tracking-widest font-bold text-indigo-700 mt-0.5">
                          ← This request
                        </div>
                      )}
                      <div className="mt-1 flex items-center justify-between text-[11px] text-slate-600">
                        <span>Cost</span>
                        <span className="tabular-nums font-semibold text-rose-700">−{t.pto_hours.toFixed(2)}h</span>
                      </div>
                      <div className="flex items-center justify-between text-[11px] text-slate-600">
                        <span>As it sits</span>
                        <span className={`tabular-nums font-bold ${shortByAsOf ? "text-rose-700" : "text-slate-800"}`}>
                          {fmtHours(t.asOfNow)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-[11px] text-slate-600">
                        <span>With accrual{t.pp_crossings_before > 0 ? ` (+${t.pp_crossings_before} PP)` : ""}</span>
                        <span className={`tabular-nums font-bold ${shortByWith ? "text-rose-700" : "text-emerald-700"}`}>
                          {fmtHours(t.withAccr)}
                        </span>
                      </div>
                      {shortByWith && (
                        <div className="mt-1 text-[10px] font-bold text-rose-800 uppercase tracking-widest">
                          Not enough hours
                        </div>
                      )}
                      {rescued && (
                        <div className="mt-1 text-[10px] font-bold text-amber-800 uppercase tracking-widest">
                          Rescued by accrual
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="text-[10px] text-slate-500 mt-1.5">
                Accrual adds {rate}h at the start of every pay period. Assumes no unpaid absence or call-out between now and each date.
              </div>
            </div>
          )}

          {!hasBalance && (
            <div className="text-xs text-slate-500 italic">
              Type a balance above to see per-request tiles with remaining hours (with and without accrual).
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TabBadge({ n }) {
  if (!n) return null;
  return (
    <span
      data-testid="tab-badge"
      className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-white text-[10px] font-bold tabular-nums leading-none"
    >
      {n > 99 ? "99+" : n}
    </span>
  );
}

export default function RequestsPage({ pendingCounts = {}, onChange }) {
  const { facilityId, facilities } = useFacility();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [kind, setKind] = useState("pto");    // "pto" | "trades" | "pickups" | "mods" | "avail" | "callouts" | "bulk-pto"
  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState({});
  const [absentTypes, setAbsentTypes] = useState({});     // { [callout_req_id]: "sick" | ... }
  const [absentCatalogue, setAbsentCatalogue] = useState([]);
  // Race-condition guard — an in-flight request from a previous `kind`
  // (e.g. slow PTO fetch) used to land AFTER a fast `pickups` fetch and
  // overwrite `items` with wrong-shape data (bug: pickup tab rendering
  // PTO records). This nonce is bumped on every fetch; only the latest
  // fetch's response is allowed to update state.
  const loadNonce = useRef(0);
  // Pay-period tab filter — only meaningful for PTO right now; other tabs
  // ignore it. Value is a pay-period id string (e.g. "pp-2026-09-13" or
  // "mo-2026-10") or "all".
  const [payPeriodTab, setPayPeriodTab] = useState("all");

  useEffect(() => {
    let alive = true;
    api.get("/absent-types").then(({ data }) => { if (alive) setAbsentCatalogue(data || []); })
       .catch(() => {});
    return () => { alive = false; };
  }, []);

  const load = useCallback(async () => {
    if (!facilityId) return;
    // Bulk PTO tab is a form, not a list — skip the fetch entirely.
    if (kind === "bulk-pto") { setItems([]); setLoading(false); return; }
    // Bump the nonce so any in-flight request from a previous kind/status
    // is invalidated — its response will be discarded when it lands.
    loadNonce.current += 1;
    const myNonce = loadNonce.current;
    // Clear immediately so a previous tab's items can't be rendered by the
    // new tab's renderer during the fetch — that mismatch caused
    // `Cannot read properties of undefined (reading 'length')` when cycling tabs.
    setItems([]);
    setLoading(true);
    const url = kind === "pto"      ? `/pto-requests?facility_id=${facilityId}&status=${status}`
              : kind === "trades"   ? `/trade-requests?facility_id=${facilityId}&status=${status}`
              : kind === "pickups"  ? `/shift-pickup-requests?facility_id=${facilityId}&status=${status}`
              : kind === "avail"    ? `/availability-edit-requests?facility_id=${facilityId}&status=${status}`
              : kind === "callouts" ? `/callout-requests?facility_id=${facilityId}&status=${status === "approved" ? "confirmed" : status}`
              :                       `/shift-modification-requests?facility_id=${facilityId}&status=${status}`;
    try {
      const { data } = await api.get(url);
      // Only accept the response if we're still the latest in-flight fetch.
      if (loadNonce.current === myNonce) { setItems(data || []); setLoading(false); }
    } catch {
      if (loadNonce.current === myNonce) { setItems([]); setLoading(false); }
    }
  }, [facilityId, kind, status]);
  useEffect(() => { load(); }, [load]);
  // Reset pay-period tab whenever the primary tab or status flips so it
  // doesn't stick on a value that no longer has any requests.
  useEffect(() => { setPayPeriodTab("all"); }, [kind, status, facilityId]);

  const decide = async (id, decision) => {
    // OT confirm gate — if the manager is APPROVING a pickup or trade that
    // would push someone into OT, force them to acknowledge before submitting.
    if (decision === "approved") {
      const req = items.find((r) => r.id === id);
      const otWarnings = [];
      if (kind === "pickups" && req?.ot_warnings?.length) {
        otWarnings.push(`${req.employee_name} → OT after this pickup:\n • ${req.ot_warnings.join("\n • ")}`);
      }
      if (kind === "trades") {
        if (req?.ot_impact_requester?.length) otWarnings.push(`${req.requester_name} → OT after trade:\n • ${req.ot_impact_requester.join("\n • ")}`);
        if (req?.ot_impact_partner?.length)   otWarnings.push(`${req.partner_name} → OT after trade:\n • ${req.ot_impact_partner.join("\n • ")}`);
      }
      if (otWarnings.length && !window.confirm(`⚠ OVERTIME WARNING — approving this will push someone past your facility's OT rules.\n\n${otWarnings.join("\n\n")}\n\nApprove anyway?`)) {
        return;
      }
    }
    try {
      const url = kind === "pto"     ? `/pto-requests/${id}`
                : kind === "trades"  ? `/trade-requests/${id}`
                : kind === "pickups" ? `/shift-pickup-requests/${id}`
                : kind === "avail"   ? `/availability-edit-requests/${id}`
                :                      `/shift-modification-requests/${id}`;
      await api.patch(url, { decision, manager_note: notes[id] || null });
      const label = kind === "pto" ? "PTO" : kind === "trades" ? "Trade" : kind === "pickups" ? "Pickup" : kind === "avail" ? "Availability edit" : "Modification";
      toast.success(`${label} ${decision}`);
      setNotes((n) => ({ ...n, [id]: "" }));
      load();
      onChange?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed");
    }
  };

  // Per-date PTO decision — lets the manager peel a bad date off a multi-day
  // request while leaving the rest pending. Backend rolls the request up to
  // approved/denied once every date has a verdict.
  const decidePtoDate = async (id, isoDate, decision) => {
    try {
      await api.patch(`/pto-requests/${id}/decide-date`, {
        date: isoDate, decision, manager_note: notes[id] || null,
      });
      toast.success(`${isoDate} ${decision}`);
      load();
      onChange?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed");
    }
  };

  // ---- PTO pay-period grouping (only meaningful for kind === "pto") ----
  // Builds a sorted list of pay-period tabs from the current PTO items so the
  // manager can jump to "October", "October 15–28", etc. Empty when kind !=
  // pto. Items get an attached `pp` object for cheap filter comparisons.
  const itemsWithPP = useMemo(() => {
    if (kind !== "pto") return items;
    return items.map((r) => {
      const firstDate = (r.dates || []).slice().sort()[0] || null;
      return { ...r, __pp: pp_for_date(firstDate, facilityId) };
    });
  }, [items, kind, facilityId]);

  const payPeriodOptions = useMemo(() => {
    if (kind !== "pto") return [];
    const map = new Map();
    for (const r of itemsWithPP) {
      const pp = r.__pp;
      if (!pp) continue;
      if (!map.has(pp.id)) map.set(pp.id, { ...pp, count: 0 });
      map.get(pp.id).count += 1;
    }
    // Sort ascending by pp start — current-first-then-future reads left-to-right.
    return [...map.values()].sort((a, b) => a.startIso.localeCompare(b.startIso));
  }, [itemsWithPP, kind]);

  // Auto-select the nearest current/future pay-period on first load or when
  // the option set changes and the previous selection is gone.
  useEffect(() => {
    if (kind !== "pto") return;
    if (payPeriodOptions.length === 0) return;
    if (payPeriodTab !== "all" && payPeriodOptions.some(p => p.id === payPeriodTab)) return;
    const todayIso = format(new Date(), "yyyy-MM-dd");
    const currentOrNext = payPeriodOptions.find(p => p.startIso >= todayIso.slice(0, 7))
                        || payPeriodOptions[0];
    setPayPeriodTab(currentOrNext?.id || "all");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payPeriodOptions, kind]);

  // The `items` used by the renderer below — filtered by the selected
  // pay-period tab when we're on the PTO list; otherwise unchanged.
  const visibleItems = useMemo(() => {
    if (kind !== "pto") return items;
    if (payPeriodTab === "all") return itemsWithPP;
    return itemsWithPP.filter(r => r.__pp?.id === payPeriodTab);
  }, [kind, items, itemsWithPP, payPeriodTab]);

  const decideCallout = async (id, decision) => {
    try {
      const body = { decision, manager_note: notes[id] || null };
      if (decision === "confirmed") {
        const t = absentTypes[id];
        if (!t) { toast.error("Pick an attendance type first"); return; }
        body.absent_type = t;
      }
      await api.patch(`/callout-requests/${id}`, body);
      toast.success(decision === "confirmed" ? "Call-out confirmed & tracked" : "Call-out denied — shift restored");
      setNotes((n) => ({ ...n, [id]: "" }));
      setAbsentTypes((n) => ({ ...n, [id]: "" }));
      load();
      onChange?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed");
    }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white" data-testid="requests-page">
      <div className="p-5 border-b border-slate-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-lg font-display font-semibold">
            <ClipboardList size={18} className="text-blue-600" /> Requests
          </div>
          <div className="text-sm text-slate-500">Review PTO and shift trade requests for your facilities.</div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Tabs value={kind} onValueChange={setKind}>
            <TabsList>
              <TabsTrigger value="pto"      data-testid="req-kind-pto">
                PTO <TabBadge n={pendingCounts.pto} />
              </TabsTrigger>
              <TabsTrigger value="callouts" data-testid="req-kind-callouts">
                Call-outs <TabBadge n={pendingCounts.callouts} />
              </TabsTrigger>
              <TabsTrigger value="trades"   data-testid="req-kind-trades">
                Trades <TabBadge n={pendingCounts.trades} />
              </TabsTrigger>
              <TabsTrigger value="pickups"  data-testid="req-kind-pickups">
                Pickups <TabBadge n={pendingCounts.pickups} />
              </TabsTrigger>
              <TabsTrigger value="mods"     data-testid="req-kind-mods">
                Modifications <TabBadge n={pendingCounts.mods} />
              </TabsTrigger>
              <TabsTrigger value="avail"    data-testid="req-kind-avail">
                Availability Edits <TabBadge n={pendingCounts.avail} />
              </TabsTrigger>
              {isAdmin && (
                <TabsTrigger value="bulk-pto" data-testid="req-kind-bulk-pto"
                             className="data-[state=active]:bg-red-600 data-[state=active]:text-white">
                  <span className="mr-1">🛡</span> Bulk PTO
                </TabsTrigger>
              )}
            </TabsList>
          </Tabs>
          <Tabs value={status} onValueChange={setStatus}>
            <TabsList>
              <TabsTrigger value="pending"  data-testid="req-tab-pending">Pending</TabsTrigger>
              <TabsTrigger value="approved" data-testid="req-tab-approved">Approved</TabsTrigger>
              <TabsTrigger value="denied"   data-testid="req-tab-denied">Denied</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      <div className="divide-y divide-slate-100">
        {kind === "bulk-pto" && isAdmin ? (
          <BulkPtoTab onSubmitted={() => { onChange?.(); }} />
        ) : (<>
        {kind === "pto" && payPeriodOptions.length > 0 && (
          <div className="px-4 py-3 border-b border-slate-200 bg-slate-50/60 overflow-x-auto" data-testid="pto-pp-tabs">
            <div className="flex items-center gap-1.5 min-w-max">
              <span className="text-[10px] uppercase tracking-widest font-black text-slate-500 mr-1">
                {MONTHLY_FACILITIES.has(facilityId) ? "Month" : "Pay period"}
              </span>
              <button
                onClick={() => setPayPeriodTab("all")}
                data-testid="pto-pp-tab-all"
                className={`text-[11px] font-semibold uppercase tracking-widest px-2.5 py-1 rounded-md border transition-colors ${
                  payPeriodTab === "all"
                    ? "bg-fuchsia-600 border-fuchsia-600 text-white"
                    : "bg-white border-slate-300 text-slate-700 hover:bg-slate-100"
                }`}
              >
                All <span className="tabular-nums ml-1 opacity-75">{itemsWithPP.length}</span>
              </button>
              {payPeriodOptions.map((pp) => (
                <button
                  key={pp.id}
                  onClick={() => setPayPeriodTab(pp.id)}
                  data-testid={`pto-pp-tab-${pp.id}`}
                  className={`text-[11px] font-semibold uppercase tracking-widest px-2.5 py-1 rounded-md border transition-colors ${
                    payPeriodTab === pp.id
                      ? "bg-fuchsia-600 border-fuchsia-600 text-white"
                      : "bg-white border-slate-300 text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  {pp.label} <span className="tabular-nums ml-1 opacity-75">{pp.count}</span>
                </button>
              ))}
            </div>
          </div>
        )}
        {loading && (
          <div className="p-8 text-center text-sm text-slate-500 flex items-center justify-center gap-2" data-testid="requests-loading">
            <span className="inline-block w-4 h-4 rounded-full border-2 border-slate-300 border-t-blue-600 animate-spin" aria-hidden />
            Loading {kind === "pto" ? "PTO" : kind === "trades" ? "trade" : kind === "pickups" ? "pickup" : kind === "avail" ? "availability edit" : kind === "callouts" ? "call-out" : "modification"} requests…
          </div>
        )}
        {!loading && visibleItems.length === 0 && (
          <div className="p-8 text-center text-sm text-slate-500">No {status} {
            kind === "pto" ? "PTO" : kind === "trades" ? "trade" : kind === "pickups" ? "pickup" : kind === "avail" ? "availability edit" : kind === "callouts" ? "call-out" : "modification"
          } requests{kind === "pto" && payPeriodTab !== "all" ? " in this pay period" : ""}.</div>
        )}
        {!loading && visibleItems.map((r) => {
          const cls = STATUS_COLORS[r.status] || STATUS_COLORS.pending;
          if (kind === "callouts") {
            const chosenType = absentTypes[r.id] || r.suggested_absent_type || "";
            return (
              <div key={r.id} className="p-4" data-testid={`callout-${r.id}`}>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-red-100 text-red-700 grid place-items-center shrink-0">
                    <PhoneCall size={18} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-medium truncate">{r.employee_name}</div>
                      <span className={`text-[11px] px-2 py-0.5 rounded-md border ${cls} font-semibold uppercase tracking-widest`}>{r.status}</span>
                      <span className="inline-flex items-center gap-1 text-[11px] text-red-800 bg-red-50 border border-red-200 rounded-md px-2 py-0.5 font-semibold uppercase tracking-widest">
                        Called out — {REASON_LABELS[r.reason] || r.reason}
                      </span>
                      <span className="inline-flex items-center gap-1 text-[11px] text-slate-600 bg-slate-100 border border-slate-200 rounded-md px-2 py-0.5">
                        <Building2 size={11} /> {facilities?.find(f => f.id === r.facility_id)?.label || r.facility_id}
                      </span>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      Called out {fmt(r.requested_at, "MMM d, yyyy HH:mm")}
                    </div>
                    <div className="mt-2 rounded-md border border-slate-200 p-2 flex flex-wrap items-center gap-3 text-sm">
                      <span className="inline-flex items-center gap-1"><CalendarIcon size={12} />{fmt(r.date, "EEE, MMM d")}</span>
                      {r.original_shift_code && (
                        <span className="inline-flex items-center rounded-md border border-blue-300 bg-blue-100 text-blue-800 px-1.5 py-0.5 text-[11px] font-bold">{r.original_shift_code}</span>
                      )}
                      {(r.original_start_time && r.original_end_time) && (
                        <span className="inline-flex items-center gap-1 text-xs text-slate-600"><Clock size={11} />{r.original_start_time}–{r.original_end_time}</span>
                      )}
                      <span className="tabular-nums text-xs text-slate-700 font-medium">{r.original_hours || 0}h</span>
                    </div>
                    {r.employee_comment && (
                      <div className="mt-2 text-sm text-slate-700 italic">&ldquo;{r.employee_comment}&rdquo;</div>
                    )}
                    <div className="mt-1 text-[11px] text-slate-500 flex items-center gap-1">
                      <AlertTriangle size={11} className="text-amber-600" />
                      Schedule already shows this as absent — pick the attendance type below to close this request.
                    </div>

                    {r.status === "pending" && (
                      <div className="mt-3 border-2 border-amber-300 bg-amber-50 rounded-xl p-3" data-testid={`callout-picker-${r.id}`}>
                        <div className="text-[11px] uppercase tracking-widest font-black text-amber-900 mb-2">Attendance type</div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                          {absentCatalogue.map((t) => (
                            <label key={t.code}
                              className={`flex items-start gap-2 p-2 rounded-lg border cursor-pointer transition-colors ${
                                chosenType === t.code
                                  ? "border-amber-500 bg-white"
                                  : "border-slate-200 bg-white hover:bg-amber-100/40"
                              }`}
                              data-testid={`callout-type-${r.id}-${t.code}`}
                            >
                              <input type="radio" name={`callout-type-${r.id}`} value={t.code}
                                checked={chosenType === t.code}
                                onChange={() => setAbsentTypes((n) => ({ ...n, [r.id]: t.code }))}
                                className="mt-0.5" />
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-semibold text-slate-800">{t.label}</div>
                                <div className="text-[11px] text-slate-500 leading-snug">{t.description}</div>
                              </div>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                    {r.status !== "pending" && r.absent_type && (
                      <div className="mt-2 text-xs text-slate-600">
                        Attendance type: <b>{absentCatalogue.find(t => t.code === r.absent_type)?.label || r.absent_type}</b>
                        {r.closed_via === "schedule" && <span className="ml-2 italic">(marked from schedule)</span>}
                      </div>
                    )}
                    {r.status !== "pending" && r.manager_note && (
                      <div className="mt-2 text-xs italic text-slate-600">Manager note: {r.manager_note}</div>
                    )}
                  </div>
                </div>
                {r.status === "pending" && (
                  <div className="mt-3 flex flex-col sm:flex-row gap-2">
                    <Textarea
                      className="sm:flex-1 min-h-[40px]"
                      placeholder="Manager note (optional, manager-only)…"
                      value={notes[r.id] || ""}
                      onChange={(e) => setNotes({ ...notes, [r.id]: e.target.value })}
                      data-testid={`callout-note-${r.id}`}
                    />
                    <div className="flex gap-2">
                      <Button className="bg-amber-600 hover:bg-amber-700"
                              onClick={() => decideCallout(r.id, "confirmed")}
                              disabled={!chosenType}
                              data-testid={`callout-confirm-${r.id}`}>
                        <Check size={14} className="mr-1" /> Confirm absent
                      </Button>
                      <Button variant="outline" className="border-red-300 text-red-700 hover:bg-red-50"
                              onClick={() => decideCallout(r.id, "denied")}
                              data-testid={`callout-deny-${r.id}`}>
                        <X size={14} className="mr-1" /> Deny & restore shift
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          }
          if (kind === "avail") {
            const cur = r.current_days || [];
            const req = r.requested_days || [];
            const byDate = {};
            cur.forEach(d => { byDate[d.date] = { ...(byDate[d.date] || {}), before: d.codes || [] }; });
            req.forEach(d => { byDate[d.date] = { ...(byDate[d.date] || {}), after:  d.codes || [] }; });
            const changed = Object.entries(byDate)
              .filter(([, v]) => JSON.stringify((v.before||[]).sort()) !== JSON.stringify((v.after||[]).sort()))
              .sort(([a],[b]) => a.localeCompare(b));
            return (
              <div key={r.id} className="p-4" data-testid={`req-avail-${r.id}`}>
                <div className="flex items-start gap-3">
                  <Avatar className="w-10 h-10 shrink-0">
                    <AvatarImage src={r.employee_avatar} />
                    <AvatarFallback>{r.employee_name?.[0] || "?"}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-semibold truncate">{r.employee_name}</div>
                      <span className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[11px] font-semibold ${cls}`}>{r.status}</span>
                      <span className="text-xs text-slate-500 tabular-nums">Pay period {r.pay_period_id?.replace("pp-","")}</span>
                    </div>
                    {r.reason && <div className="mt-1 text-sm text-slate-700 italic">&ldquo;{r.reason}&rdquo;</div>}
                    <div className="mt-2 text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Requested changes ({changed.length} day{changed.length===1?"":"s"})</div>
                    <div className="mt-1 grid sm:grid-cols-2 gap-1.5">
                      {changed.length === 0 && <div className="text-xs text-slate-500">No actual changes.</div>}
                      {changed.map(([d, v]) => (
                        <div key={d} className="rounded-md border border-slate-200 bg-slate-50 p-2 text-xs" data-testid={`req-avail-diff-${d}`}>
                          <div className="font-semibold tabular-nums">{fmt(d, "EEE, MMM d")}</div>
                          <div className="mt-0.5 text-slate-600">
                            <span className="line-through opacity-60">{(v.before || []).join("·") || "—"}</span>
                            <span className="mx-1">→</span>
                            <span className="font-bold text-blue-800">{(v.after || []).join("·") || "—"}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                {r.status === "pending" && (
                  <div className="mt-3 flex flex-col sm:flex-row gap-2">
                    <Textarea
                      className="sm:flex-1 min-h-[40px]"
                      placeholder="Manager note (optional)…"
                      value={notes[r.id] || ""}
                      onChange={(e) => setNotes({ ...notes, [r.id]: e.target.value })}
                      data-testid={`avail-note-${r.id}`}
                    />
                    <div className="flex gap-2">
                      <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => decide(r.id, "approved")} data-testid={`avail-approve-${r.id}`}>
                        <Check size={14} className="mr-1" /> Approve
                      </Button>
                      <Button variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" onClick={() => decide(r.id, "denied")} data-testid={`avail-deny-${r.id}`}>
                        <X size={14} className="mr-1" /> Deny
                      </Button>
                    </div>
                  </div>
                )}
                {r.status !== "pending" && r.manager_note && (
                  <div className="mt-2 text-xs italic text-slate-600">Manager note: {r.manager_note}</div>
                )}
              </div>
            );
          }
          if (kind === "mods") {
            const typeLabel = { arrive_late: "Arrive late", leave_early: "Leave early", long_lunch: "Long lunch" }[r.type] || r.type;
            const isLunch = r.type === "long_lunch";
            return (
              <div key={r.id} className="p-4" data-testid={`mod-${r.id}`}>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-orange-100 text-orange-700 grid place-items-center shrink-0">
                    <Wrench size={18} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-medium truncate">{r.employee_name}</div>
                      <span className={`text-[11px] px-2 py-0.5 rounded-md border ${cls} font-semibold uppercase tracking-widest`}>{r.status}</span>
                      <span className="inline-flex items-center gap-1 text-[11px] text-orange-800 bg-orange-50 border border-orange-200 rounded-md px-2 py-0.5 font-semibold uppercase tracking-widest">
                        <Wrench size={11} /> {typeLabel}
                      </span>
                      <span className="inline-flex items-center gap-1 text-[11px] text-slate-600 bg-slate-100 border border-slate-200 rounded-md px-2 py-0.5">
                        <Building2 size={11} /> {facilities?.find(f => f.id === r.facility_id)?.label || r.facility_id}
                      </span>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      Requested {fmt(r.requested_at, "MMM d, yyyy HH:mm")}
                    </div>
                    <div className="mt-2 rounded-md border border-slate-200 p-2 flex flex-wrap items-center gap-3 text-sm">
                      <span className="inline-flex items-center gap-1"><CalendarIcon size={12} />{fmt(r.date, "EEE, MMM d")}</span>
                      <span className="inline-flex items-center rounded-md border border-blue-300 bg-blue-100 text-blue-800 px-1.5 py-0.5 text-[11px] font-bold">{r.shift_code}</span>
                      {r.shift_label && <span className="text-slate-600 text-xs">{r.shift_label}</span>}
                    </div>
                    <div className="mt-2 grid sm:grid-cols-2 gap-2 text-sm">
                      <div className="rounded-md border border-slate-200 p-2">
                        <div className="text-[10px] uppercase tracking-widest text-slate-500">Currently</div>
                        <div className="tabular-nums font-medium">
                          {r.original_start} – {r.original_end} · {r.original_hours || 0}h
                        </div>
                      </div>
                      <div className="rounded-md border border-orange-200 bg-orange-50 p-2">
                        <div className="text-[10px] uppercase tracking-widest text-orange-700">Requested</div>
                        <div className="tabular-nums font-medium">
                          {isLunch
                            ? `${r.original_start} – ${r.original_end} (lunch ${r.gap_start}–${r.gap_end})`
                            : `${r.new_start} – ${r.new_end}`}
                        </div>
                        <div className="text-[11px] text-orange-800 mt-0.5">
                          Coverage needed {r.gap_start}–{r.gap_end} ({r.gap_hours}h)
                        </div>
                      </div>
                    </div>
                    {r.reason && <div className="mt-2 text-sm text-slate-700 italic">&ldquo;{r.reason}&rdquo;</div>}
                  </div>
                </div>
                {r.status === "pending" && (
                  <div className="mt-3 flex flex-col sm:flex-row gap-2">
                    <Textarea className="sm:flex-1 min-h-[40px]"
                      placeholder="Manager note (optional)…"
                      value={notes[r.id] || ""}
                      onChange={(e) => setNotes({ ...notes, [r.id]: e.target.value })}
                      data-testid={`mod-note-${r.id}`}
                    />
                    <div className="flex gap-2">
                      <Button className="bg-sky-600 hover:bg-sky-700" onClick={() => decide(r.id, "approved")} data-testid={`mod-approve-${r.id}`}>
                        <Check size={14} className="mr-1" /> Approve
                      </Button>
                      <Button variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" onClick={() => decide(r.id, "denied")} data-testid={`mod-deny-${r.id}`}>
                        <X size={14} className="mr-1" /> Deny
                      </Button>
                    </div>
                  </div>
                )}
                {r.status !== "pending" && r.manager_note && (
                  <div className="mt-2 text-xs italic text-slate-600">Manager note: {r.manager_note}</div>
                )}
              </div>
            );
          }
          if (kind === "pickups") {
            const ots = r.ot_warnings || [];
            const hasOT = ots.length > 0;
            return (
              <div key={r.id} className="p-4" data-testid={`pickup-${r.id}`}>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-700 grid place-items-center shrink-0">
                    <HandHelping size={18} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-medium truncate">{r.employee_name}</div>
                      <span className={`text-[11px] px-2 py-0.5 rounded-md border ${cls} font-semibold uppercase tracking-widest`}>{r.status}</span>
                      <span className="inline-flex items-center gap-1 text-[11px] text-slate-600 bg-slate-100 border border-slate-200 rounded-md px-2 py-0.5">
                        <Building2 size={11} /> {facilities?.find(f => f.id === r.facility_id)?.label || r.facility_id}
                      </span>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      Requested {fmt(r.requested_at, "MMM d, yyyy HH:mm")}
                    </div>
                    <div className="mt-2 rounded-md border border-slate-200 p-2 flex flex-wrap items-center gap-3 text-sm">
                      <span className="inline-flex items-center gap-1"><CalendarIcon size={12} />{fmt(r.date, "EEE, MMM d")}</span>
                      <span className="inline-flex items-center rounded-md border border-blue-300 bg-blue-100 text-blue-800 px-1.5 py-0.5 text-[11px] font-bold">{r.shift_code}</span>
                      {r.shift_label && <span className="text-slate-600 text-xs">{r.shift_label}</span>}
                      {(r.start_time && r.end_time) && (
                        <span className="inline-flex items-center gap-1 text-xs text-slate-600"><Clock size={11} />{r.start_time}–{r.end_time}</span>
                      )}
                      <span className="tabular-nums text-xs text-slate-700 font-medium">{r.hours || 0}h</span>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`pickup-flags-${r.id}`}>
                      <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border font-semibold ${hasOT ? "bg-red-50 border-red-300 text-red-800" : "bg-emerald-50 border-emerald-300 text-emerald-800"}`}>
                        {hasOT ? <><AlertTriangle size={11} /> Yes, this request will create Overtime.</>
                               : <><Check size={11} /> No, this request will not create overtime on approval.</>}
                      </span>
                    </div>
                    {hasOT && (
                      <div className="mt-2 rounded-lg border-2 border-red-400 bg-red-50 p-3 shadow-sm" data-testid={`pickup-ot-warning-${r.id}`}>
                        <div className="flex items-center gap-2 mb-1.5">
                          <AlertTriangle size={16} className="text-red-600" />
                          <div className="font-black uppercase tracking-widest text-[11px] text-red-800">
                            Overtime warning · approving will break OT rules
                          </div>
                        </div>
                        <ul className="text-[12px] text-red-900 font-medium leading-tight list-disc list-inside space-y-0.5">
                          {ots.map((w, i) => (<li key={i}>{w}</li>))}
                        </ul>
                        <div className="mt-1.5 text-[10px] text-red-700 italic">
                          You'll be asked to confirm before Approve goes through.
                        </div>
                      </div>
                    )}
                    {r.reason && <div className="mt-2 text-sm text-slate-700 italic">&ldquo;{r.reason}&rdquo;</div>}
                  </div>
                </div>
                {r.status === "pending" && (
                  <div className="mt-3 flex flex-col sm:flex-row gap-2">
                    <Textarea
                      className="sm:flex-1 min-h-[40px]"
                      placeholder="Manager note (optional)…"
                      value={notes[r.id] || ""}
                      onChange={(e) => setNotes({ ...notes, [r.id]: e.target.value })}
                      data-testid={`pickup-note-${r.id}`}
                    />
                    <div className="flex gap-2">
                      <Button className="bg-sky-600 hover:bg-sky-700" onClick={() => decide(r.id, "approved")} data-testid={`pickup-approve-${r.id}`}>
                        <Check size={14} className="mr-1" /> Approve & assign
                      </Button>
                      <Button variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" onClick={() => decide(r.id, "denied")} data-testid={`pickup-deny-${r.id}`}>
                        <X size={14} className="mr-1" /> Deny
                      </Button>
                    </div>
                  </div>
                )}
                {r.status !== "pending" && r.manager_note && (
                  <div className="mt-2 text-xs italic text-slate-600">Manager note: {r.manager_note}</div>
                )}
              </div>
            );
          }
          if (kind === "trades") {
            const otReq = (r.ot_impact_requester || []).length > 0;
            const otPar = (r.ot_impact_partner || []).length > 0;
            return (
              <div key={r.id} className="p-4" data-testid={`trade-${r.id}`}>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-700 grid place-items-center shrink-0">
                    <Repeat2 size={18} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-medium truncate">
                        {r.requester_name} <ArrowLeftRight size={12} className="inline mx-1 text-slate-400" /> {r.partner_name}
                      </div>
                      <span className={`text-[11px] px-2 py-0.5 rounded-md border ${cls} font-semibold uppercase tracking-widest`}>
                        {r.status}
                      </span>
                      <span className="inline-flex items-center gap-1 text-[11px] text-slate-600 bg-slate-100 border border-slate-200 rounded-md px-2 py-0.5">
                        <Building2 size={11} /> {facilities?.find(f => f.id === r.facility_id)?.label || r.facility_id}
                      </span>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      Requested {fmt(r.requested_at, "MMM d, yyyy HH:mm")}
                      {typeof r.notice_hours === "number" && ` · ${r.notice_hours}h notice`}
                    </div>

                    <div className="mt-3 grid sm:grid-cols-2 gap-2 text-sm">
                      <div className="rounded-md border border-slate-200 p-2">
                        <div className="text-[10px] uppercase tracking-widest text-slate-500">{r.requester_name} gives up</div>
                        <div className="font-medium">{fmt(r.requester_date, "EEE, MMM d")}</div>
                        <div className="text-xs text-slate-600">{r.requester_shift_code} · {r.requester_shift_hours || 0}h</div>
                      </div>
                      <div className="rounded-md border border-slate-200 p-2">
                        <div className="text-[10px] uppercase tracking-widest text-slate-500">{r.partner_name} gives up</div>
                        <div className="font-medium">{fmt(r.partner_date, "EEE, MMM d")}</div>
                        <div className="text-xs text-slate-600">{r.partner_shift_code} · {r.partner_shift_hours || 0}h</div>
                      </div>
                    </div>

                    <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`trade-flags-${r.id}`}>
                      <Flag ok={!!r.seventy_two_hour_notice} label="72h notice" />
                      <Flag ok={!!r.same_pay_period} label="Same pay period" />
                      <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border font-semibold ${(otReq || otPar) ? "bg-red-50 border-red-300 text-red-800" : "bg-emerald-50 border-emerald-300 text-emerald-800"}`}>
                        {(otReq || otPar) ? <><AlertTriangle size={11} /> Yes, this request will create Overtime.</>
                                          : <><Check size={11} /> No, this request will not create overtime on approval.</>}
                      </span>
                    </div>
                    {(otReq || otPar) && (
                      <div className="mt-2 rounded-lg border-2 border-red-400 bg-red-50 p-3 shadow-sm" data-testid={`trade-ot-warning-${r.id}`}>
                        <div className="flex items-center gap-2 mb-1.5">
                          <AlertTriangle size={16} className="text-red-600" />
                          <div className="font-black uppercase tracking-widest text-[11px] text-red-800">
                            Overtime warning · approving will break OT rules
                          </div>
                        </div>
                        <ul className="text-[12px] text-red-900 font-medium leading-tight list-disc list-inside space-y-0.5">
                          {otReq && r.ot_impact_requester.map((w, i) => (
                            <li key={`r${i}`}><b>{r.requester_name}:</b> {w}</li>
                          ))}
                          {otPar && r.ot_impact_partner.map((w, i) => (
                            <li key={`p${i}`}><b>{r.partner_name}:</b> {w}</li>
                          ))}
                        </ul>
                        <div className="mt-1.5 text-[10px] text-red-700 italic">
                          You'll be asked to confirm before Approve goes through.
                        </div>
                      </div>
                    )}
                    {r.reason && <div className="mt-2 text-sm text-slate-700 italic">&ldquo;{r.reason}&rdquo;</div>}
                  </div>
                </div>
                {r.status === "pending" && (
                  <div className="mt-3 flex flex-col sm:flex-row gap-2">
                    <Textarea
                      className="sm:flex-1 min-h-[40px]"
                      placeholder="Manager note (optional)…"
                      value={notes[r.id] || ""}
                      onChange={(e) => setNotes({ ...notes, [r.id]: e.target.value })}
                      data-testid={`trade-note-${r.id}`}
                    />
                    <div className="flex gap-2">
                      <Button className="bg-sky-600 hover:bg-sky-700" onClick={() => decide(r.id, "approved")} data-testid={`trade-approve-${r.id}`}>
                        <Check size={14} className="mr-1" /> Approve & swap
                      </Button>
                      <Button variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" onClick={() => decide(r.id, "denied")} data-testid={`trade-deny-${r.id}`}>
                        <X size={14} className="mr-1" /> Deny
                      </Button>
                    </div>
                  </div>
                )}
                {r.status !== "pending" && r.manager_note && (
                  <div className="mt-2 text-xs italic text-slate-600">Manager note: {r.manager_note}</div>
                )}
              </div>
            );
          }
          // PTO layout — one clean tile per request.
          const anyOverlap = r.overlap_approved > 0 || r.overlap_pending > 0;
          const rc = r.review_context || null;
          const isPending = r.status === "pending";
          const lowNotice = r.days_notice < 14;
          const noticeColor = lowNotice
            ? "text-red-700 bg-red-50 border-red-300"
            : "text-emerald-700 bg-emerald-50 border-emerald-300";
          return (
            <div key={r.id} className="p-4" data-testid={`req-${r.id}`}>
              <div className="rounded-xl border-2 border-slate-200 bg-white shadow-sm overflow-hidden">
                <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px]">
                  {/* LEFT: single info tile — name, dates, hours, notice, counts */}
                  <div className="p-4 lg:p-5">
                    {/* Header row: big employee name + status/facility pills */}
                    <div className="flex items-start gap-3 flex-wrap">
                      <Avatar className="w-11 h-11 shrink-0">
                        <AvatarFallback>{r.employee_name?.[0] || "?"}</AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <div className="text-xl font-bold text-slate-900 truncate leading-tight">
                          {r.employee_name}
                        </div>
                        <div className="mt-1 flex items-center gap-2 flex-wrap">
                          <span className={`text-[11px] px-2 py-0.5 rounded-md border ${cls} font-semibold uppercase tracking-widest`}>
                            {r.status}
                          </span>
                          <span className="inline-flex items-center gap-1 text-[11px] text-slate-600 bg-slate-100 border border-slate-200 rounded-md px-2 py-0.5">
                            <Building2 size={11} /> {r.facility_label}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Body lines — clean labeled rows */}
                    <div className="mt-4 space-y-2 text-sm">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="text-slate-500 font-medium min-w-[170px]">Dates requested:</span>
                        <span className="flex flex-wrap gap-1">
                          {(r.dates || []).map((d) => {
                            const dept = rc?.own_dept_by_date?.[d];
                            return (
                              <span
                                key={d}
                                className="text-xs rounded-md bg-fuchsia-50 border border-fuchsia-200 text-fuchsia-800 px-1.5 py-0.5 inline-flex items-center gap-1"
                                data-testid={`pto-date-${r.id}-${d}`}
                                title={dept ? `Would normally work: ${dept}` : "Not scheduled that day"}
                              >
                                {fmt(d, "EEE, MMM d")}
                                {dept && (
                                  <span className="text-[9px] uppercase tracking-widest font-bold text-fuchsia-600 bg-white border border-fuchsia-200 rounded px-1">
                                    {dept}
                                  </span>
                                )}
                              </span>
                            );
                          })}
                        </span>
                      </div>

                      <div className="flex items-baseline gap-2">
                        <span className="text-slate-500 font-medium min-w-[170px]">Total PTO hours needed:</span>
                        <span className="font-bold tabular-nums text-slate-900" data-testid={`pto-hours-${r.id}`}>
                          {Number(r.pto_hours || 0).toFixed(2)}h
                        </span>
                        <span className="text-xs text-slate-500">
                          ({r.work_days || 0} of {(r.dates || []).length} day{(r.dates || []).length === 1 ? "" : "s"} scheduled)
                        </span>
                      </div>

                      <div className="flex items-baseline gap-2">
                        <span className="text-slate-500 font-medium min-w-[170px]">Requested on:</span>
                        <span className="text-slate-800">{fmt(r.requested_at, "MMM d, yyyy 'at' h:mm a")}</span>
                      </div>

                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="text-slate-500 font-medium min-w-[170px]">Days notice:</span>
                        <span
                          data-testid={`req-notice-${r.id}`}
                          className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md border font-semibold uppercase tracking-widest ${noticeColor}`}
                        >
                          {lowNotice ? <AlertTriangle size={11} /> : <Check size={11} />}
                          {r.days_notice}d
                        </span>
                        {lowNotice && (
                          <span className="text-[11px] text-red-700 italic">
                            Caution — less than 14 days notice
                          </span>
                        )}
                      </div>

                      {/* Department rank (uses master schedule to detect same-dept peers) */}
                      {rc && (rc.dept_summary?.length ?? 0) > 0 && (
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <span className="text-slate-500 font-medium min-w-[170px]">Requests in this department:</span>
                          <span className="flex flex-wrap gap-x-3 gap-y-1">
                            {rc.dept_summary.map((d) => {
                              const total = d.approved + d.pending;
                              const busy = total >= 1;
                              const chipClass = busy
                                ? "bg-red-50 border-red-300 text-red-800"
                                : "bg-emerald-50 border-emerald-300 text-emerald-800";
                              return (
                                <span key={d.label} className="inline-flex items-center gap-1.5 text-sm">
                                  <b className="text-slate-900">{d.label}</b>:
                                  <span
                                    data-testid={`req-dept-count-${d.label}`}
                                    className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md border font-semibold uppercase tracking-widest ${chipClass}`}
                                  >
                                    {busy ? <AlertTriangle size={11} /> : <Check size={11} />}
                                    {total} other
                                  </span>
                                  {busy && (
                                    <span className="text-[11px] text-red-700 italic">
                                      Caution — {total} other PTO {total === 1 ? "request" : "requests"} already approved or pending for {d.label}
                                    </span>
                                  )}
                                  <span className="ml-1 text-[11px] text-slate-500">
                                    (this would be #{d.this_request_rank} in {d.label})
                                  </span>
                                </span>
                              );
                            })}
                          </span>
                        </div>
                      )}

                      {/* Facility-wide, DATE-scoped rank */}
                      {rc && (
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <span className="text-slate-500 font-medium min-w-[170px]">Requests in the facility:</span>
                          <span className="inline-flex items-center gap-1.5 text-sm">
                            <span
                              data-testid={`req-facility-count-${r.id}`}
                              className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md border font-semibold uppercase tracking-widest ${
                                rc.facility_overlap_count >= 1
                                  ? "bg-red-50 border-red-300 text-red-800"
                                  : "bg-emerald-50 border-emerald-300 text-emerald-800"
                              }`}
                            >
                              {rc.facility_overlap_count >= 1 ? <AlertTriangle size={11} /> : <Check size={11} />}
                              {rc.facility_overlap_count} other
                            </span>
                            {rc.facility_overlap_count >= 1 && (
                              <span className="text-[11px] text-red-700 italic">
                                Caution — {rc.facility_overlap_count} other PTO {rc.facility_overlap_count === 1 ? "request" : "requests"} already approved or pending on these dates
                              </span>
                            )}
                            <span className="ml-1 text-[11px] text-slate-500">
                              (this would be #{rc.facility_request_index})
                            </span>
                          </span>
                        </div>
                      )}

                      {/* Trained employees available to pick up (currently off,
                          AND wouldn't create an OT violation on the requester's shift) */}
                      {rc && (
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <span className="text-slate-500 font-medium min-w-[170px]">Trained open employees:</span>
                          <span className="text-sm inline-flex items-center gap-2 flex-wrap">
                            <b data-testid={`req-trained-open-${r.id}`} className="tabular-nums text-slate-900">{rc.trained_free_min}</b>
                            <span className="inline-flex items-center gap-1.5">
                              <span
                                data-testid={`req-trained-pd-${r.id}`}
                                className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md border font-semibold uppercase tracking-widest bg-amber-50 border-amber-300 text-amber-800"
                              >
                                {rc.trained_pd_min ?? 0} PD
                              </span>
                              <span
                                data-testid={`req-trained-ben-${r.id}`}
                                className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md border font-semibold uppercase tracking-widest bg-sky-50 border-sky-300 text-sky-800"
                              >
                                {rc.trained_benefitted_min ?? 0} Ben
                              </span>
                            </span>
                            <span className="ml-1 text-[11px] text-slate-500">
                              (trained in this shift's department, off on every requested date, and wouldn't trip an OT rule if they picked up the shift)
                            </span>
                          </span>
                        </div>
                      )}

                      {r.reason && (
                        <div className="mt-2 pt-2 border-t border-slate-100 text-sm text-slate-700 italic">
                          &ldquo;{r.reason}&rdquo;
                        </div>
                      )}
                      {anyOverlap && (
                        <div className="text-[11px] text-amber-700 flex items-center gap-1 pt-1">
                          <AlertTriangle size={11} />
                          {r.overlap_approved} already approved · {r.overlap_pending} still pending on these dates
                        </div>
                      )}
                    </div>

                    {/* Per-date breakdown — only rendered for multi-day
                        requests so a single-date PTO stays compact. Each
                        column is a date; rows show dept, dept peers count,
                        facility peers count, trained-off pool, plus a
                        per-date Approve / Deny for pending requests. */}
                    {isPending && rc && (rc.per_date_breakdown?.length || 0) > 1 && (
                      <div className="mt-4 pt-3 border-t border-slate-200">
                        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold mb-2">
                          Per-date breakdown
                        </div>
                        <div className="overflow-x-auto rounded-lg border border-slate-200">
                          <table className="min-w-full text-sm">
                            <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
                              <tr>
                                <th className="text-left px-3 py-2 font-bold">Detail</th>
                                {rc.per_date_breakdown.map((row) => (
                                  <th key={row.date} className="text-left px-3 py-2 font-bold">
                                    {fmt(row.date, "EEE, MMM d")}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                              <tr>
                                <td className="px-3 py-2 text-slate-500">Department</td>
                                {rc.per_date_breakdown.map((row) => (
                                  <td key={row.date} className="px-3 py-2">
                                    {row.department ? (
                                      <span className="text-[11px] uppercase font-bold tracking-widest text-fuchsia-700 bg-fuchsia-50 border border-fuchsia-200 rounded px-1.5 py-0.5">
                                        {row.department}
                                      </span>
                                    ) : (
                                      <span className="text-slate-400 text-xs">Off/unscheduled</span>
                                    )}
                                  </td>
                                ))}
                              </tr>
                              <tr>
                                <td className="px-3 py-2 text-slate-500">Others in this dept</td>
                                {rc.per_date_breakdown.map((row) => (
                                  <td key={row.date} className="px-3 py-2 tabular-nums">
                                    <b>{row.dept_others_approved + row.dept_others_pending}</b>
                                    {row.department && (
                                      <span className="ml-1 text-[11px] text-slate-500">
                                        (#{row.dept_this_rank})
                                      </span>
                                    )}
                                  </td>
                                ))}
                              </tr>
                              <tr>
                                <td className="px-3 py-2 text-slate-500">Others in facility</td>
                                {rc.per_date_breakdown.map((row) => (
                                  <td key={row.date} className="px-3 py-2 tabular-nums">
                                    <b>{row.facility_others_approved + row.facility_others_pending}</b>
                                    <span className="ml-1 text-[11px] text-slate-500">
                                      (#{row.facility_this_rank})
                                    </span>
                                  </td>
                                ))}
                              </tr>
                              <tr>
                                <td className="px-3 py-2 text-slate-500">Trained &amp; OT-safe</td>
                                {rc.per_date_breakdown.map((row) => (
                                  <td key={row.date} className="px-3 py-2 tabular-nums">
                                    <b>{row.trained_free}</b>
                                    <span className="ml-1 text-[11px] text-slate-500">
                                      ({row.trained_pd ?? 0} PD · {row.trained_benefitted ?? 0} Ben)
                                    </span>
                                  </td>
                                ))}
                              </tr>
                              <tr className="bg-slate-50/60">
                                <td className="px-3 py-2 text-slate-500">Decision</td>
                                {rc.per_date_breakdown.map((row) => {
                                  const dd = (r.date_decisions || {})[row.date];
                                  return (
                                    <td key={row.date} className="px-3 py-2">
                                      {dd ? (
                                        <span className={`text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded ${
                                          dd.status === "approved"
                                            ? "bg-emerald-100 text-emerald-800 border border-emerald-300"
                                            : "bg-red-100 text-red-800 border border-red-300"
                                        }`}>
                                          {dd.status}
                                        </span>
                                      ) : (
                                        <div className="flex gap-1">
                                          <button
                                            onClick={() => decidePtoDate(r.id, row.date, "approved")}
                                            data-testid={`pto-date-approve-${r.id}-${row.date}`}
                                            className="text-[10px] uppercase font-bold tracking-widest px-2 py-1 rounded bg-sky-600 hover:bg-sky-700 text-white"
                                          >
                                            Approve
                                          </button>
                                          <button
                                            onClick={() => decidePtoDate(r.id, row.date, "denied")}
                                            data-testid={`pto-date-deny-${r.id}-${row.date}`}
                                            className="text-[10px] uppercase font-bold tracking-widest px-2 py-1 rounded border border-red-300 text-red-700 hover:bg-red-50"
                                          >
                                            Deny
                                          </button>
                                        </div>
                                      )}
                                    </td>
                                  );
                                })}
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        <div className="mt-2 text-[11px] text-slate-500 italic">
                          Tip: peel individual days off with per-date Approve / Deny above, or decide the whole request at once with the buttons below.
                        </div>
                      </div>
                    )}
                  </div>

                  {/* RIGHT: PTO calculator, only for pending */}
                  {isPending && rc ? (
                    <div className="border-t lg:border-t-0 lg:border-l border-slate-200 bg-slate-50">
                      <PtoCalculator
                        ctx={rc}
                        thisRequestId={r.id}
                        requestId={r.id}
                      />
                    </div>
                  ) : null}
                </div>
              </div>

              {r.status === "pending" && (
                <div className="mt-3 flex flex-col sm:flex-row gap-2">
                  <Textarea
                    className="sm:flex-1 min-h-[40px]"
                    placeholder="Manager note (optional, emailed to the employee)…"
                    value={notes[r.id] || ""}
                    onChange={(e) => setNotes({ ...notes, [r.id]: e.target.value })}
                    data-testid={`req-note-${r.id}`}
                  />
                  <div className="flex gap-2">
                    <Button className="bg-sky-600 hover:bg-sky-700" onClick={() => decide(r.id, "approved")} data-testid={`req-approve-${r.id}`}>
                      <Check size={14} className="mr-1" /> Approve
                    </Button>
                    <Button variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" onClick={() => decide(r.id, "denied")} data-testid={`req-deny-${r.id}`}>
                      <X size={14} className="mr-1" /> Deny
                    </Button>
                  </div>
                </div>
              )}
              {r.status !== "pending" && r.manager_note && (
                <div className="mt-2 text-xs italic text-slate-600">Manager note: {r.manager_note}</div>
              )}
            </div>
          );
        })}
        </>)}
      </div>
    </div>
  );
}
